import React, { useState, useEffect, useCallback } from "react";
import type { StatusPageState } from "./types";
import { config } from "./config";
import { loadStatusPageState } from "./api/client";
import { Header } from "./components/Header";
import { StatusHero } from "./components/StatusHero";
import { IncidentFeed } from "./components/IncidentFeed";
import { ServiceList } from "./components/ServiceList";
import { Footer } from "./components/Footer";
import { AlertCircle, RefreshCw } from "lucide-react";

// Placeholder blocks shown before the first Kuma response, so the page never claims
// "no incidents" or "telemetry unavailable" while it simply hasn't loaded yet.
const LoadingSkeleton: React.FC = () => (
  <div className="space-y-6 animate-pulse" aria-busy="true" aria-label="Betöltés">
    <div className="bg-white border border-[#dedbd4] rounded-[0.375rem] h-[74px]" />
    <div className="space-y-3">
      <div className="h-7 w-64 max-w-full rounded-[0.25rem] bg-[#ebe8e1]" />
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="bg-white border border-[#dedbd4] rounded-[0.375rem] h-[120px]"
        />
      ))}
    </div>
  </div>
);

export const App: React.FC = () => {
  const [pageState, setPageState] = useState<StatusPageState>({
    overallStatus: "loading",
    overallUptimePercent: 100,
    groups: [],
    incident: null,
    maintenanceList: [],
    lastUpdated: new Date(),
    isMock: config.isMockDefault,
    isLoading: true,
    error: null,
  });

  const fetchData = useCallback(
    async (isManual = false) => {
      if (isManual) {
        setPageState((prev) => ({ ...prev, isLoading: true }));
      }
      try {
        const nextState = await loadStatusPageState();
        setPageState(nextState);
      } catch (err) {
        setPageState((prev) => ({
          ...prev,
          isLoading: false,
          overallStatus: prev.groups.length > 0 ? prev.overallStatus : "unknown",
          error:
            err instanceof Error
              ? `Nem sikerült kapcsolatot létesíteni a telemetriai kiszolgálóval: ${err.message}`
              : "Nem sikerült kapcsolatot létesíteni a telemetriai kiszolgálóval.",
        }));
      }
    },
    []
  );

  useEffect(() => {
    fetchData(true);
  }, [fetchData]);

  useEffect(() => {
    const timer = setInterval(() => {
      fetchData(false);
    }, config.refreshIntervalSeconds * 1000);

    return () => clearInterval(timer);
  }, [fetchData]);

  const handleManualRefresh = () => {
    fetchData(true);
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#faf9f6] text-[#1b2a47]">
      {/* App Shell Header */}
      <Header
        lastUpdated={pageState.overallStatus === "loading" ? null : pageState.lastUpdated}
      />

      {/* Main Container */}
      <main className="flex-1 max-w-6xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Error / Fallback Notification */}
        {pageState.error && (
          <div className="rounded-[0.375rem] border border-[#edd6a4] bg-[#faf0d9] p-3.5 flex items-center justify-between gap-3 text-xs text-[#775219]">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-[#775219] flex-shrink-0" />
              <span>{pageState.error}</span>
            </div>
            <button
              type="button"
              onClick={handleManualRefresh}
              className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-[0.25rem] bg-white hover:bg-[#f3f1ec] text-[#775219] border border-[#edd6a4] transition-colors"
            >
              <RefreshCw className="w-3 h-3" />
              Újrapróbálás
            </button>
          </div>
        )}

        {/* Overall System Health Banner */}
        <StatusHero
          status={pageState.overallStatus}
          uptimePercent={pageState.overallUptimePercent}
          groups={pageState.groups}
        />

        {pageState.overallStatus === "loading" ? (
          <LoadingSkeleton />
        ) : (
          <>
            {/* Active Incident Feed & Maintenance Notifications */}
            <IncidentFeed
              incident={pageState.incident}
              maintenanceList={pageState.maintenanceList}
            />

            {/* Component / Subsystem List */}
            <ServiceList groups={pageState.groups} />
          </>
        )}
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
};
